/**
 * Spread pins that sit on the same spot so each one can be clicked.
 * A single pin at a location stays put.
 */

const DEFAULT_EPSILON = 1e-7;
/** ~40px at mid zoom; small fixed degree ring for pure (map-free) spiderfy. */
const DEFAULT_RADIUS_DEGREES = 0.00035;

export type SpiderfyOptions = {
	epsilon?: number;
	radiusDegrees?: number;
};

export type SpiderfyPoint = {
	coordinates: [number, number];
	id: string;
};

const coordKey = (coordinates: [number, number], epsilon: number): string => {
	const [lng, lat] = coordinates;
	const scale = 1 / epsilon;
	return `${Math.round(lng * scale) / scale},${Math.round(lat * scale) / scale}`;
};

/**
 * When several sensors share a coordinate, place them in a small ring so
 * each pin can be clicked. A lone sensor stays where it is.
 */
export const spiderfyCoordinates = (
	points: SpiderfyPoint[],
	options: SpiderfyOptions = {},
): Map<string, [number, number]> => {
	const epsilon = options.epsilon ?? DEFAULT_EPSILON;
	const radiusDegrees = options.radiusDegrees ?? DEFAULT_RADIUS_DEGREES;
	const result = new Map<string, [number, number]>();

	const groups = new Map<string, SpiderfyPoint[]>();
	for (const point of points) {
		const key = coordKey(point.coordinates, epsilon);
		const group = groups.get(key) ?? [];
		group.push(point);
		groups.set(key, group);
	}

	for (const group of groups.values()) {
		if (group.length === 1) {
			const only = group[0]!;
			result.set(only.id, only.coordinates);
			continue;
		}

		const [originLng, originLat] = group[0]!.coordinates;
		const n = group.length;

		group.forEach((point, index) => {
			const angle = (2 * Math.PI * index) / n - Math.PI / 2;
			const lng = originLng + radiusDegrees * Math.cos(angle);
			const lat = originLat + radiusDegrees * Math.sin(angle);
			result.set(point.id, [lng, lat]);
		});
	}

	return result;
};
